/**
 * Phase 6-C / Journey B — Client Portal Dashboard.
 *
 * White-label landing once the magic-link session is established. The page is
 * built around a branded auto-summary hero: a greeting + a one-line read on
 * "what needs you" derived from the proposals list, coloured from the shop's
 * brand config (`shop_brand_color`, via `getPortalBrand`). Below the hero:
 *  - Proposals waiting your sign-off (from /proposals, filtered to pending)
 *  - Performance overview (placeholder — Phase 7 fills in)
 *  - Reports link to `/client-portal/reports`
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { RootState } from '../../store/store';
import {
  clearClientPortalSession,
  setClientPortalIdentity,
} from '../../store/reducers/authSlice';
import {
  ClientPortalProposal,
  getMe,
  getProposals,
} from '../../lib/api/client-portal/client-portal-api';
import { buildPortalSummary, getPortalBrand } from './brand';

const ClientPortalDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const accessToken = useSelector(
    (s: RootState) => s.auth.clientPortal.accessToken
  );
  const identity = useSelector((s: RootState) => s.auth.clientPortal.identity);

  const [proposals, setProposals] = useState<ClientPortalProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    (async () => {
      try {
        // Refresh identity + proposals in parallel.
        const [fresh, props] = await Promise.all([
          getMe(accessToken),
          getProposals(accessToken),
        ]);
        if (cancelled) return;
        dispatch(setClientPortalIdentity(fresh));
        setProposals(props);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[ClientPortalDashboard] load failed', err);
        if (!cancelled) setError('Could not load your portal data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const handleSignOut = () => {
    dispatch(clearClientPortalSession());
    navigate('/client-portal/expired', { replace: true });
  };

  const brand = useMemo(() => getPortalBrand(identity), [identity]);

  const pendingProposals = useMemo(
    () =>
      proposals.filter((p) => {
        const s = (p?.status as string | undefined)?.toLowerCase?.();
        return !s || s === 'pending' || s === 'awaiting_signoff';
      }),
    [proposals]
  );

  const summary = useMemo(
    () => buildPortalSummary(pendingProposals.length),
    [pendingProposals.length]
  );

  const firstName = (identity?.name || '').trim().split(/\s+/)[0] || '';

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f6f8' }}>
      {/* ---------- White-label brand bar ---------- */}
      <Box sx={{ backgroundColor: brand.primary, color: brand.onPrimary }}>
        <Container maxWidth='lg' sx={{ py: 1.75 }}>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            spacing={2}
          >
            <Stack direction='row' spacing={1.5} alignItems='center'>
              <Avatar
                sx={{
                  bgcolor:
                    brand.onPrimary === '#ffffff'
                      ? 'rgba(255,255,255,0.18)'
                      : 'rgba(0,0,0,0.08)',
                  color: brand.onPrimary,
                  width: 36,
                  height: 36,
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                {brand.initials}
              </Avatar>
              <Stack spacing={0}>
                <Typography
                  variant='caption'
                  sx={{ opacity: 0.75, lineHeight: 1.2 }}
                >
                  Client portal
                </Typography>
                <Typography
                  variant='subtitle1'
                  component='h1'
                  sx={{ fontWeight: 700, lineHeight: 1.2 }}
                >
                  {brand.shopName}
                </Typography>
              </Stack>
            </Stack>
            <Button
              onClick={handleSignOut}
              size='small'
              variant='outlined'
              sx={{
                color: brand.onPrimary,
                borderColor:
                  brand.onPrimary === '#ffffff'
                    ? 'rgba(255,255,255,0.5)'
                    : 'rgba(0,0,0,0.3)',
                '&:hover': {
                  borderColor: brand.onPrimary,
                  backgroundColor:
                    brand.onPrimary === '#ffffff'
                      ? 'rgba(255,255,255,0.1)'
                      : 'rgba(0,0,0,0.05)',
                },
              }}
            >
              Sign out
            </Button>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth='lg' sx={{ py: { xs: 3, md: 4 } }}>
        {error && (
          <Alert severity='warning' sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Stack direction='row' alignItems='center' spacing={2} sx={{ py: 6 }}>
            <CircularProgress size={22} sx={{ color: brand.primary }} />
            <Typography color='text.secondary'>
              Loading your portal…
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={3}>
            {/* ---------- Auto-summary hero ---------- */}
            <Paper
              elevation={0}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                color: brand.onPrimary,
                background: `linear-gradient(135deg, ${brand.primary} 0%, ${brand.primary} 60%, ${brand.primary}cc 100%)`,
              }}
            >
              <Typography
                variant='body2'
                sx={{ opacity: 0.85, mb: 0.5, fontWeight: 500 }}
              >
                {firstName ? `Welcome back, ${firstName}` : 'Welcome back'}
              </Typography>
              <Typography
                variant='h4'
                component='p'
                sx={{ fontWeight: 700, lineHeight: 1.2, mb: 1 }}
              >
                {summary.headline}
              </Typography>
              <Typography
                variant='body1'
                sx={{ opacity: 0.9, maxWidth: 560 }}
              >
                {summary.sub}
              </Typography>
            </Paper>

            {/* ---------- Proposals waiting sign-off ---------- */}
            <Paper
              elevation={0}
              variant='outlined'
              sx={{ p: 3, borderRadius: 3 }}
            >
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                sx={{ mb: 1 }}
              >
                <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                  Waiting for your sign-off
                </Typography>
                <Chip
                  label={pendingProposals.length}
                  size='small'
                  sx={{
                    fontWeight: 700,
                    bgcolor:
                      pendingProposals.length > 0
                        ? brand.softBg
                        : 'action.hover',
                    color:
                      pendingProposals.length > 0
                        ? brand.primary
                        : 'text.secondary',
                  }}
                />
              </Stack>
              <Divider sx={{ mb: 1 }} />
              {pendingProposals.length === 0 ? (
                <Typography variant='body2' color='text.secondary'>
                  Nothing pending. We'll email you when there's something to
                  review.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {pendingProposals.map((p) => (
                    <ListItem key={p.id} disableGutters>
                      <ListItemText
                        primary={p.title || `Proposal ${p.id}`}
                        secondary={p.created_at || undefined}
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>

            {/* ---------- Performance overview placeholder ---------- */}
            <Paper
              elevation={0}
              variant='outlined'
              sx={{ p: 3, borderRadius: 3 }}
            >
              <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 0.5 }}>
                Performance overview
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Coming soon — your spend, ROAS, and conversions across all
                channels will appear here.
              </Typography>
            </Paper>

            {/* ---------- Reports link ---------- */}
            <Paper
              elevation={0}
              variant='outlined'
              sx={{ p: 3, borderRadius: 3 }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent='space-between'
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={2}
              >
                <Box>
                  <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                    Reports
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Weekly performance summaries from your agency.
                  </Typography>
                </Box>
                <Button
                  component={Link}
                  to='/client-portal/reports'
                  variant='contained'
                  disableElevation
                  sx={{
                    bgcolor: brand.primary,
                    color: brand.onPrimary,
                    '&:hover': { bgcolor: brand.primary, filter: 'brightness(0.94)' },
                  }}
                >
                  View reports
                </Button>
              </Stack>
            </Paper>
          </Stack>
        )}
      </Container>
    </Box>
  );
};

export default ClientPortalDashboard;
